import axios from '@fs/zion-axios'

// Notification Preference Service (NPS). Calls go through @fs/zion-axios, which attaches the Bearer session token for FamilySearch
// domains (so the endpoints resolve the user from auth — no userId param) and routes to the correct environment host.
const BASE = '/service/messaging/notification-prefs'

// NPS uses a vendored media type; zion-axios does not set it for us, so we pass it per request.
const NPS_MEDIA_TYPE = 'application/vnd.familysearch.nps.v1+json'
const npsHeaders = { Accept: NPS_MEDIA_TYPE, 'Content-Type': NPS_MEDIA_TYPE }

// Feature flag gating the NPS-backed notifications feature. Off in prod (endpoints not deployed there
// yet). Read with useFeatureFlag(NOTIFICATIONS_FEATURE_FLAG).isOn.
export const NOTIFICATIONS_FEATURE_FLAG = 'discovery_together_notificationPreferences'

// Display profiles agreed at integration time (from GET /lookups/display-profiles). The member variant
// adds the Temple category. These control which categories/channels the display endpoint returns.
export const DISPLAY_PROFILE = {
  default: 'together_app',
  member: 'together_app_member',
}

// Push default-subscription profiles for first-consent seeding (from GET /lookups/initialization-profiles).
export const PUSH_INIT_PROFILE = {
  default: 'push_together_app',
  member: 'push_together_app_member',
}

// The Together push channel. Both the member and non-member profiles use this same channel id (the member
// profile just adds the Temple category), so the presence of any row for it means the user has already
// been seeded for Together push.
export const PUSH_CHANNEL = 'push_together_app'

// Audit sources (from GET /lookups/pref-sources): a user toggling their own settings vs. seeding the
// default push subscriptions when they consent to notifications in the app.
export const PREF_SOURCE = 'user_settings'
export const INIT_SOURCE = 'app_push_consent'

/**
 * Consolidated notification prefs for the UI, filtered to a display profile (DISPLAY_PROFILE.member or DISPLAY_PROFILE.default).
 * Returns categories, their channels, and the user's current subscription state for each — localized via the Accept-Language header
 * (set automatically by zion-axios). Response shape: { userId, categories: [{ categoryId, name,
 * description, displayOrder, channels: [{ channel, name, subscribed, displayOrder }] }] }.
 */
export const getDisplayNotificationPrefs = (profile) =>
  axios.get(`${BASE}/display/notification-prefs`, { params: { profile }, headers: npsHeaders })

/**
 * The user's raw notification preference rows (not filtered by display profile). Used to detect whether
 * the user already has Together push prefs before seeding defaults. Response: array of pref rows, each
 * with at least { categoryId, channel, optedIn }.
 */
export const getUserPrefs = () => axios.get(`${BASE}/prefs`, { headers: npsHeaders })

/**
 * Persists a single channel subscription change for a category (hits bulk endpoint but with only one item).
 */
export const updateNotificationPref = ({ categoryId, channel, optedIn }) =>
  axios.put(
    `${BASE}/prefs`,
    { prefs: [{ categoryId, channel, optedIn, source: PREF_SOURCE }] },
    { headers: npsHeaders }
  )

/**
 * Seeds the default push subscriptions for the app the first time the user allows notifications.
 * `profile` is one of PUSH_INIT_PROFILE (PUSH_INIT_PROFILE.default or PUSH_INIT_PROFILE.member).
 */
export const initializeNotificationPrefs = (profile) =>
  axios.post(`${BASE}/prefs/initialize`, null, {
    params: { profiles: profile, source: INIT_SOURCE, value: true },
    headers: npsHeaders,
  })
